import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { DirectMessageAuthService } from '../../../core/services/direct-message-auth.service';
import { CrmMessagingService } from './crm-messaging.service';
import { CrmThread } from '../models/crm-thread.model';

/**
 * One shared, app-wide subscription to unread SMS threads, keyed by
 * normalized (digits-only) phone number - so the sidebar and every CRM
 * module's board can show unread badges without each subscribing to the
 * whole crm_threads collection separately. There's no stored link from a
 * thread to a specific CRM record (threads are phone-number-only), so a
 * board cross-references its own rows' phone fields against unreadFor().
 *
 * Listens to SMS threads only (channel == 'sms'), since crm_threads also
 * holds the full HubSpot email history; unread counts are filtered in JS.
 */
@Injectable({ providedIn: 'root' })
export class UnreadSmsService {
  private counts = new BehaviorSubject<Record<string, number>>({});
  readonly counts$ = this.counts.asObservable();
  readonly totalUnread$ = this.counts$.pipe(map((c) => Object.values(c).reduce((a, b) => a + b, 0)));

  /** Every SMS thread, newest activity first - the floating SMS inbox
   *  lists these. Same single listener as the counts above. */
  private threads = new BehaviorSubject<CrmThread[]>([]);
  readonly threads$ = this.threads.asObservable();

  constructor(
    private crmMessagingService: CrmMessagingService,
    private directMessageAuthService: DirectMessageAuthService
  ) {
    // listThreads() needs a signed-in Firebase user or the security rules
    // reject it - and onSnapshot doesn't retry after a permission-denied,
    // it just dies silently (only the console.error in listThreads() shows
    // it). This service is constructed early (injected into the persistent
    // sidebar), often before anything else has triggered sign-in, so unlike
    // InboxComponent/SmsThreadComponent it can't assume sign-in already
    // happened - it has to wait on ensureSignedIn() itself first.
    this.directMessageAuthService.ensureSignedIn().subscribe(() => {
      this.crmMessagingService.listThreadsByChannel('sms', (threads) => {
        const counts: Record<string, number> = {};
        const sms = threads.filter((thread) => thread.channel === 'sms');
        this.threads.next(sms.slice().sort((a, b) => this.millis(b.updatedAt) - this.millis(a.updatedAt)));
        threads.forEach((thread) => {
          if (thread.channel !== 'sms') return;
          const key = this.last9(thread.externalAddress ?? '');
          if (key && (thread.unreadCount ?? 0) > 0) {
            counts[key] = thread.unreadCount as number;
          }
        });
        this.counts.next(counts);
      });
    });
  }

  /** Unread count for one phone number, in whatever format the caller has it. */
  unreadFor (phone: string | null | undefined): number {
    const key = this.last9(phone ?? '');
    return key ? this.counts.value[key] ?? 0 : 0;
  }

  private millis (value: any): number {
    if (typeof value?.toMillis === 'function') return value.toMillis();
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /** Local vs Ringotel-normalized numbers for the same contact often differ
   *  in country-code prefix (e.g. '0400000000' vs '61400000000') - comparing
   *  just the last 9 digits is the same tolerant match already used for
   *  phone->name lookups elsewhere (CrmContactGroupsController::phoneLookup()). */
  private last9 (phone: string): string {
    return phone.replace(/\D/g, '').slice(-9);
  }
}
